/**
 * AI Rapportage — types & DTO's (PR-8, Ronde 3).
 *
 * Zie `docs/specs/ronde-3/06-ai-rapportage-module.md` voor de full spec.
 *
 * Pipeline: collector → prompt → Claude → service.generateRapport →
 * `AiRapportage` rij in DB → PDF (pdf-generator.ts) → e-mail (email.ts).
 *
 * De DTO-shapes hier matchen 1:1 met de Prisma-models `AiRapportage` en
 * `AiRapportageConfig`, plus de Claude-output-shape uit
 * `apps/api/src/services/ai-rapportage/service.ts` (`AnalyseResult`).
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export const AI_RAPPORTAGE_TYPES = [
  'MANAGEMENT',
  'KLANTGEZONDHEID',
  'PROJECT_RENDABILITEIT',
  'HR_MAANDRAPPORT',
  'CAPACITEIT_FORECAST',
  'FACTURATIE_CASHFLOW',
  'VOERTUIG_TCO',
  'INTERCOMPANY',
] as const

export type AiRapportageType = (typeof AI_RAPPORTAGE_TYPES)[number]

export const AI_RAPPORTAGE_STATUSES = [
  'GEGENEREERD',
  'PDF_GEREED',
  'VERZONDEN',
  'MISLUKT',
] as const

export type AiRapportageStatus = (typeof AI_RAPPORTAGE_STATUSES)[number]

export const AI_VERGELIJK_PERIODES = [
  'VORIGE_MAAND',
  'VORIG_KWARTAAL',
  'ZELFDE_MAAND_VORIG_JAAR',
] as const

export type AiVergelijkPeriode = (typeof AI_VERGELIJK_PERIODES)[number]

// ─── Labels ──────────────────────────────────────────────────────────────────

export const AI_RAPPORTAGE_TYPE_LABELS: Record<AiRapportageType, string> = {
  MANAGEMENT:            'Management',
  KLANTGEZONDHEID:       'Klantgezondheid',
  PROJECT_RENDABILITEIT: 'Project Rendabiliteit',
  HR_MAANDRAPPORT:       'HR Maandrapport',
  CAPACITEIT_FORECAST:   'Capaciteit Forecast',
  FACTURATIE_CASHFLOW:   'Facturatie & Cashflow',
  VOERTUIG_TCO:          'Voertuig TCO',
  INTERCOMPANY:          'Intercompany',
}

/** Icon-emoji per type (spec §11). */
export const AI_RAPPORTAGE_TYPE_ICONS: Record<AiRapportageType, string> = {
  MANAGEMENT:            '📊',
  KLANTGEZONDHEID:       '💚',
  PROJECT_RENDABILITEIT: '📈',
  HR_MAANDRAPPORT:       '👥',
  CAPACITEIT_FORECAST:   '📅',
  FACTURATIE_CASHFLOW:   '💰',
  VOERTUIG_TCO:          '🚗',
  INTERCOMPANY:          '🔄',
}

export const AI_RAPPORTAGE_STATUS_LABELS: Record<AiRapportageStatus, string> = {
  GEGENEREERD: 'Gegenereerd',
  PDF_GEREED:  'PDF gereed',
  VERZONDEN:   'Verzonden',
  MISLUKT:     'Mislukt',
}

export const AI_VERGELIJK_PERIODE_LABELS: Record<AiVergelijkPeriode, string> = {
  VORIGE_MAAND:            'Vorige maand',
  VORIG_KWARTAAL:          'Vorig kwartaal',
  ZELFDE_MAAND_VORIG_JAAR: 'Zelfde maand vorig jaar',
}

// ─── Foutcategorie (FO-053) ──────────────────────────────────────────────────
//
// Een mislukt rapport (`status === 'MISLUKT'`) draagt een leesbare
// `errorMessage`. Die mappen we naar een afgeleide categorie zodat de UI
// kan tonen of het aan de configuratie, de drukte (rate-limit) of de inhoud
// ligt. De API zet `errorCategorie` bij voorkeur al weg (preciezer, want hij
// kent het echte error-object); voor oudere rijen zonder dat veld leidt de UI
// de categorie af uit `errorMessage` via {@link categoriseerAiFoutmelding}.
//
// Bewust een vrije string-set met deze const als enige bron van waarheid (geen
// Prisma-enum): zo hoeft een nieuwe categorie geen `ALTER TYPE ADD VALUE`-
// migratie, wat op de live enum-tabellen kan blijven hangen.

export const AI_FOUT_CATEGORIES = [
  'TEGOED',
  'CONFIG_AUTH',
  'RATE_LIMIT',
  'NETWERK',
  'PARSE',
  'AFGEKAPT',
  'DATA',
  'PDF',
  'ONBEKEND',
] as const

export type AiFoutCategorie = (typeof AI_FOUT_CATEGORIES)[number]

/**
 * wk34 — categorieën die niet aan één document/rapport liggen maar aan de
 * AI-dienst zelf. Mail-processors (inkoop, onderhoudsrapport) markeren zo'n
 * ingest NIET terminaal FOUT: de rij blijft NIEUW en de volgende tick probeert
 * opnieuw. Eén gedeelde bron zodat de pijplijnen niet uiteenlopen.
 */
export const SYSTEMISCHE_AI_FOUT_CATEGORIEEN: ReadonlySet<AiFoutCategorie> = new Set([
  'TEGOED',
  'RATE_LIMIT',
  'NETWERK',
])

/**
 * wk34 — na hoe lang aanhoudende NETWERK-fouten op dezelfde ingest tóch naar
 * FOUT escaleren. Vangnet tegen een "poison document" (bv. een te grote
 * gescande PDF die structureel de per-call-time-out overschrijdt): zonder
 * plafond zou die eeuwig elke cron-tick een Claude-call verbranden. Een echte
 * dienststoring langer dan dit venster is zeldzaam; de geëscaleerde rijen zijn
 * dan zichtbaar in de FOUT-lijst en handmatig opnieuw aan te bieden.
 */
export const AI_NETWERK_FOUT_NA_MS = 6 * 60 * 60 * 1000

/** Korte badge-tekst per categorie. */
export const AI_FOUT_CATEGORIE_LABELS: Record<AiFoutCategorie, string> = {
  TEGOED:      'AI-tegoed op',
  CONFIG_AUTH: 'Configuratie',
  RATE_LIMIT:  'Rate-limit',
  NETWERK:     'Verbinding',
  PARSE:       'Verwerking',
  AFGEKAPT:    'Afgekapt',
  DATA:        'Brongegevens',
  PDF:         'PDF',
  ONBEKEND:    'Onbekend',
}

/** Leesbare uitleg per categorie (voor de gebruiker, géén technische jargon). */
export const AI_FOUT_CATEGORIE_UITLEG: Record<AiFoutCategorie, string> = {
  TEGOED:      'Het AI-tegoed is op. De verwerking hervat automatisch zodra het tegoed is aangevuld - er gaat niets verloren.',
  CONFIG_AUTH: 'AI niet geconfigureerd - neem contact op met de beheerder.',
  RATE_LIMIT:  'De AI-dienst is tijdelijk te druk (rate-limit). Probeer het later opnieuw.',
  NETWERK:     'De AI-dienst was tijdelijk niet bereikbaar (verbindingsfout). Het systeem probeert het later automatisch opnieuw.',
  PARSE:       'De AI gaf een onverwacht antwoord dat niet verwerkt kon worden. Opnieuw proberen lost dit meestal op.',
  AFGEKAPT:    'Het AI-antwoord was te groot en is afgekapt, ook met een verhoogd budget. Probeer het opnieuw of kies een kortere periode.',
  DATA:        'Er ging iets mis bij het verzamelen van de brongegevens voor dit rapport.',
  PDF:         'De analyse is gelukt, maar de PDF kon niet worden opgesteld. Probeer het opnieuw.',
  ONBEKEND:    'Er ging iets mis bij het genereren van dit rapport.',
}

/**
 * FO-402 (batch-50) — tekstpatronen die op een uitgeput Anthropic-tegoed
 * wijzen. Anthropic levert credit-uitputting als HTTP 400 `invalid_request`
 * met "Your credit balance is too low ..." in de message: matchen doen we dus
 * op de MELDING, nooit op de statuscode.
 *
 * De Nederlandse varianten staan er bewust bij: bestaande FOUT-rijen (o.a. de
 * inkoop-mail-ingests) dragen onze eigen vertaalde melding ("AI-tegoed is op
 * - ...") en moeten door de hervat-actie net zo goed herkend worden als een
 * verse SDK-fout. Exporteren zodat requeue-queries dezelfde bron gebruiken.
 */
export const AI_TEGOED_FOUT_PATRONEN = [
  'credit balance',
  'credit too low',
  'insufficient credit',
  'insufficient_quota',
  'ai-tegoed is op',
  'tegoed is op',
] as const

/**
 * Leid de foutcategorie af uit een `errorMessage`-string. Puur (geen side-
 * effects), zodat zowel de API (als fallback) als de UI hem kan gebruiken.
 *
 * De volgorde van de checks is bewust: TEGOED vóór CONFIG_AUTH (de melding kan
 * naast de credit-tekst ook een auth-achtig woord bevatten, en de remedie -
 * tegoed aanvullen - verschilt wezenlijk van een configuratiefout), daarna
 * config/auth en rate-limit (die herkennen we aan SDK-statuscodes/keywords) en
 * tot slot de door ons gecontroleerde prefixen ("Data verzamelen faalde",
 * "PDF-generatie faalde", "Parse-error").
 */
export function categoriseerAiFoutmelding(
  errorMessage: string | null | undefined,
): AiFoutCategorie {
  if (!errorMessage) return 'ONBEKEND'
  const m = errorMessage.toLowerCase()

  const heeft = (...needles: string[]) => needles.some((n) => m.includes(n))

  // FO-402 — tegoed op. Vóór CONFIG_AUTH: "credit balance" hoorde daar
  // voorheen bij, maar heeft een eigen remedie (tegoed aanvullen) en opent de
  // centrale AI-pauze.
  if (heeft(...AI_TEGOED_FOUT_PATRONEN)) return 'TEGOED'

  // Statuscodes met word-boundaries zodat "404ms" e.d. niet vals matcht; de
  // tekstuele keywords zijn ondubbelzinnig.
  if (
    /\b(401|403|404)\b/.test(m) ||
    heeft(
      'anthropic_api_key',
      'api key',
      'api-key',
      'x-api-key',
      'ontbreekt in environment',
      'niet geconfigureerd',
      'authentication',
      'unauthorized',
      'forbidden',
      'permission denied',
      'not_found_error',
    )
  ) {
    return 'CONFIG_AUTH'
  }

  if (
    /\b(429|529)\b/.test(m) ||
    heeft('rate limit', 'rate_limit', 'overloaded', 'te veel ai-calls')
  ) {
    return 'RATE_LIMIT'
  }

  // Afkapping (FO-148): het antwoord raakte het tokenbudget (stop_reason
  // max_tokens). Vóór de PARSE-check, want de melding kan "JSON" bevatten.
  if (heeft('afgekapt', 'max_tokens', 'max tokens')) return 'AFGEKAPT'

  if (heeft('pdf')) return 'PDF'
  if (heeft('data verzamelen', 'collector', 'collect')) return 'DATA'
  if (heeft('parse-error', 'parse error', 'geen json', 'json')) return 'PARSE'

  // wk34 — verbindingsfouten (@anthropic-ai/sdk APIConnectionTimeoutError:
  // "Request timed out."; APIConnectionError: "Connection error."; plus de
  // Node-varianten). Transiënt: callers behandelen dit als "later opnieuw",
  // nooit als terminale document-fout. Ná de gecontroleerde prefixen (een
  // "Data verzamelen faalde: ... timed out" blijft DATA) en bewust zonder het
  // losse woord 'timeout' (te breed, zie de word-boundary-test).
  if (
    heeft(
      'timed out',
      'connection error',
      'econnreset',
      'econnrefused',
      'etimedout',
      'socket hang up',
      'fetch failed',
      'network error',
      'eai_again',
      'apiconnection',
    )
  ) {
    return 'NETWERK'
  }

  return 'ONBEKEND'
}

// ─── Analyse-output sub-DTOs (1:1 met service.ts AnalyseResult) ──────────────

export type AandachtspuntPrioriteit = 'KRITIEK' | 'HOOG' | 'NORMAAL' | 'LAAG'

/**
 * Eén concrete observatie uit het rapport. Wordt per prioriteit gegroepeerd
 * weergegeven met titel + beschrijving + suggestie/actie.
 */
export interface AandachtspuntDTO {
  prioriteit:   AandachtspuntPrioriteit
  titel:        string
  beschrijving: string
  /** Concrete vervolg-actie (Claude-output gebruikt `actie`, NIET `suggestie`). */
  actie:        string
  /** Vrij-tekst categorie ("financieel" / "personeel" / "operationeel" / …). */
  categorie:    string
}

/** Eén KPI-tegel — label + waarde + trend-pijl + toelichting. */
export interface KerncijferDTO {
  label:        string
  waarde:       string
  trend:        'up' | 'down' | 'flat'
  toelichting:  string
}

export interface TrendDTO {
  onderwerp:    string
  richting:     'positief' | 'negatief' | 'neutraal'
  beschrijving: string
}

export interface AnomalieDTO {
  metric:           string
  verwacht:         string
  werkelijk:        string
  afwijking:        string
  mogelijkeOorzaak: string
}

/**
 * De `analyseSecties`-JSON in de DB-rij. NIET een `[{titel,content,…}]` array
 * zoals de spec §7.2 suggereerde — de service heeft gekozen voor een object
 * per categorie (kerncijfers / trends / anomalieën / aanbevelingen), wat
 * eenvoudiger te renderen is.
 */
export interface AnalyseSectiesDTO {
  kerncijfers:   KerncijferDTO[]
  trends:        TrendDTO[]
  anomalieen:    AnomalieDTO[]
  aanbevelingen: string[]
}

// ─── Lijst-item ──────────────────────────────────────────────────────────────

/** Compact item zoals teruggegeven in `GET /ai-rapportage` (lijst-endpoint). */
export interface AiRapportageListItemDTO {
  id:             string
  type:           AiRapportageType
  status:         AiRapportageStatus
  periodeStart:   string
  periodeEind:    string
  periodeLabel:   string
  samenvatting:   string
  pdfPath:        string | null
  verzondenAan:   string[]
  verzondenOp:    string | null
  manualTrigger:  boolean
  createdAt:      string
  entityId:       string | null
  /** Alleen gevuld bij `status === 'MISLUKT'`. */
  errorMessage:   string | null
  /** Afgeleide categorie van de fout (FO-053). Null voor oudere rijen. */
  errorCategorie: AiFoutCategorie | null
}

// ─── Detail ──────────────────────────────────────────────────────────────────

/** Volledig rapport-detail zoals teruggegeven in `GET /ai-rapportage/:id`. */
export interface AiRapportageDetailDTO {
  id:              string
  type:            AiRapportageType
  status:          AiRapportageStatus
  periodeStart:    string
  periodeEind:     string
  periodeLabel:    string
  entityId:        string | null
  /** Compact snapshot van wat naar Claude is gestuurd. */
  inputData:       unknown
  inputTokens:     number | null
  samenvatting:    string
  /** Object met kerncijfers / trends / anomalieen / aanbevelingen — zie AnalyseSectiesDTO. */
  analyseSecties:  AnalyseSectiesDTO
  aandachtspunten: AandachtspuntDTO[]
  outputTokens:    number | null
  modelUsed:       string
  duurMs:          number | null
  pdfPath:         string | null
  pdfGeneratedAt:  string | null
  verzondenAan:    string[]
  verzondenOp:     string | null
  manualTrigger:   boolean
  createdById:     string | null
  errorMessage:    string | null
  /** Afgeleide categorie van de fout (FO-053). Null voor oudere rijen. */
  errorCategorie:  AiFoutCategorie | null
  createdAt:       string
  updatedAt:       string
}

// ─── Config ──────────────────────────────────────────────────────────────────

/** Config-rij per rapport-type, beheerbaar onder `/instellingen/ai-rapportage`. */
export interface AiRapportageConfigDTO {
  id:              string
  type:            AiRapportageType
  enabled:         boolean
  /** Standaard-cron-uitdrukkingen zie spec §1 / scheduler.ts comment. */
  cronExpression:  string
  emailOntvangers: string[]
  ccOntvangers:    string[]
  /** Type-specifieke drempel-waarden voor anomalie-detectie (key→number). */
  drempels:        Record<string, number> | null
  vergelijkMet:    AiVergelijkPeriode
  createdAt:       string
  updatedAt:       string
}

/** Patch-payload voor PATCH /ai-rapportage/config/:type (ADMIN only). */
export interface AiRapportageConfigPatchDTO {
  enabled?:         boolean
  cronExpression?:  string
  emailOntvangers?: string[]
  ccOntvangers?:    string[]
  drempels?:        Record<string, number>
  vergelijkMet?:    AiVergelijkPeriode
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

/** Compact dashboard-payload uit `GET /ai-rapportage/dashboard`. */
export interface AiRapportageDashboardDTO {
  management:        AiRapportageDetailDTO | null
  hr:                AiRapportageDetailDTO | null
  beschikbareTypes:  readonly AiRapportageType[]
  laatsteGeneratie:  Record<string, string | null>
}

// ─── Genereer-payload ────────────────────────────────────────────────────────

/** Body voor POST /ai-rapportage/genereer (manual trigger). */
export interface GenereerRapportPayloadDTO {
  type:         AiRapportageType
  /** ISO datetime. */
  periodeStart: string
  /** ISO datetime. */
  periodeEind:  string
  /** Optionele entity-scope (BV-specifiek rapport). */
  entityId?:    string
}

// ─── Verzend-payload ─────────────────────────────────────────────────────────

/** Body voor POST /ai-rapportage/:id/verzenden. */
export interface VerzendRapportPayloadDTO {
  extraOntvangers?: string[]
  ccOntvangers?:    string[]
}

// ─── Config-/health-check (FO-053) ────────────────────────────────────────────

// ─── Centrale AI-pauze (FO-402, batch-50) ─────────────────────────────────────

/**
 * De actieve AI-pauze (circuit-breaker) zoals de UI hem toont. Aanwezig zodra
 * een call als {@link AiFoutCategorie} `TEGOED` faalde; de probe-cron heft hem
 * op zodra het tegoed weer werkt.
 */
export interface AiPauzeStatusDTO {
  /** Wanneer de pauze inging (ISO, UTC). Tevens de episode-sleutel. */
  gestartOp:            string
  /** Neutrale reden (nooit documentinhoud), bv. de foutmelding-samenvatting. */
  reden:                string
  /** Welke functie de uitputting constateerde, bv. `inkoop-mail-processor`. */
  functie:              string
  /** Wanneer de laatste alarm-/herinneringsmail is aangeboden (ISO) of null. */
  laatsteAlarmOp:       string | null
  /** Aantal inkoop-mail-ingests dat op verwerking wacht (statusgedreven). */
  aantalWachtendeIngests: number
}

/** Resultaat van `GET /ai-rapportage/ai-status`. Lekt nooit de sleutel zelf. */
export interface AiConfigStatusDTO {
  configured: boolean
  model:      string
  /**
   * FO-402 — actieve AI-pauze, of null wanneer AI gewoon draait. Zolang deze
   * gevuld is weigert de wrapper elke niet-probe-call direct (geen API-call)
   * en blijven inkoop-ingests op NIEUW staan.
   */
  pauze:      AiPauzeStatusDTO | null
}

/** Uitkomst van de handmatige ADMIN-actie "probeer nu" (FO-402 §5). */
export interface AiTegoedProbeResultDTO {
  /** Stond er überhaupt een pauze open? */
  pauzeWasActief: boolean
  /** Is de pauze door deze probe opgeheven? */
  hervat:         boolean
  /** Aantal inkoop-ingests dat door de hervat-actie terug op NIEUW is gezet. */
  gerequeued:     number
  /** Categorie van de mislukte probe-call (alleen bij `hervat: false`). */
  categorie:      AiFoutCategorie | null
  /** Leesbare toelichting voor de gebruiker. */
  melding:        string
}

/** Beheerbare instelling: waar gaat de tegoed-alarmmail heen (FO-402 §5). */
export interface AiTegoedInstellingDTO {
  /** Ingesteld alarm-adres, of null wanneer nog niet geconfigureerd. */
  alarmEmail: string | null
  /**
   * Staat de alarmMAIL aan? Uit betekent: geen mail, maar de postvak-melding
   * aan de beheerders blijft altijd staan - een stilgevallen verwerkingsketen
   * mag nooit volledig onzichtbaar worden. Default aan.
   */
  actief:     boolean
  /**
   * Waar het effectieve adres vandaan komt: `INSTELLING` (deze rij), `ENV`
   * (fallback `AI_TEGOED_ALARM_EMAIL`) of `ADMINS` (laatste vangnet: alle
   * actieve beheerders). Zo ziet de beheerder of de seed al gedraaid heeft.
   */
  herkomst:   'INSTELLING' | 'ENV' | 'ADMINS'
}

/** Resultaat van `POST /ai-rapportage/ai-test` ("AI-verbinding testen"). */
export interface AiPingResultDTO {
  ok:         boolean
  model:      string
  categorie?: AiFoutCategorie
  message?:   string
}
